import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HistoryIcon, SearchIcon, ClockIcon } from "lucide-react";

export default function Historico() {
  const [query, setQuery] = useState("");
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get("/cycles", { params: query ? { no_serie: query } : {} });
      setCycles(data);
    } catch (err) {
      // ignore
    } finally { setLoading(false); }
  };

  useEffect(() => { search(); }, []);

  const days = (a, b) => {
    if (!a || !b) return null;
    return Math.round((new Date(b) - new Date(a)) / 86400000);
  };

  return (
    <div className="p-4 md:p-8 max-w-[1500px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1">Histórico · Ciclos por Ativo</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Histórico de Ativos</h1>
          <p className="text-sm text-muted-foreground mt-2">Pesquise um N° de Série para ver todos os giros (ciclos fechados/abertos).</p>
        </div>
        <HistoryIcon className="w-12 h-12 text-[#00E5FF]" strokeWidth={1.2} />
      </header>

      <form onSubmit={search} className="mb-6 flex gap-3 max-w-2xl">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="hist-search"
            value={query}
            onChange={(e) => setQuery(e.target.value.toUpperCase())}
            placeholder="N° Série (ex: TQ-5000-1001) ou vazio para todos"
            className="pl-9 rounded-none bg-[#14151A] font-mono h-11"
          />
        </div>
        <Button type="submit" disabled={loading} data-testid="hist-submit" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] h-11 px-6 font-bold uppercase tracking-wider">Buscar</Button>
      </form>

      <div className="space-y-3">
        {cycles.length === 0 && (
          <Card className="bg-[#14151A] border-border rounded-none p-8 text-center text-muted-foreground">
            Nenhum ciclo encontrado.
          </Card>
        )}
        {cycles.map((c) => {
          const ciclo_aberto = !c.data_retorno;
          const dias = days(c.data_saida, c.data_retorno);
          return (
            <Card key={c.id} className={`bg-[#14151A] border-border rounded-none p-5 border-l-4 ${ciclo_aberto ? "border-l-[#00E5FF]" : "border-l-[#34C759]"}`} data-testid={`cycle-${c.id}`}>
              <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="font-mono text-2xl font-bold text-[#FFD60A]">{c.no_serie}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Lote <span className="font-mono text-white">{c.no_lote}</span> · {c.nome_produto}</div>
                  <div className="text-[11px] font-mono text-[#00E5FF] mt-0.5">{c.codigo_produto && `#${c.codigo_produto} `}{c.funcao_quimica}</div>
                </div>
                {ciclo_aberto ? (
                  <Badge className="rounded-none border bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40 font-bold">🔵 EM TRÂNSITO / NO CLIENTE (Ciclo em Aberto)</Badge>
                ) : (
                  <Badge className="rounded-none border bg-[#34C759]/15 text-[#34C759] border-[#34C759]/40 font-bold">🟢 CICLO CONCLUÍDO (Retornado)</Badge>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                <Field label="Cliente" value={c.cliente} accent />
                <Field label="N° Pedido / NF" value={[c.no_pedido, c.nf_produto].filter(Boolean).join(" · ")} mono />
                <Field label="Qtd Envasada" value={c.qtd_envasado ? `${c.qtd_envasado} L` : null} mono />
                <Field label="Validade Lote" value={c.data_validade_lote} mono />
                <Field label="Data Envase" value={c.data_envase && new Date(c.data_envase).toLocaleString("pt-BR")} mono />
                <Field label="Data Saída" value={c.data_saida && new Date(c.data_saida).toLocaleString("pt-BR")} mono />
                <Field label="Data Retorno" value={c.data_retorno ? new Date(c.data_retorno).toLocaleString("pt-BR") : "—"} mono />
                <Field label="Tempo na rua" value={dias != null ? <span className="text-[#FFD60A]"><ClockIcon className="w-3 h-3 inline mr-1" />{dias} dia(s)</span> : null} />
              </div>

              <div className="mt-3 pt-3 border-t border-border/60 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Field label="Motorista" value={c.motorista} />
                <Field label="Placa" value={c.placa} mono />
                <Field label="Lacres" value={(c.lacres || []).join(" · ")} mono />
                <Field label="Usuário Envase" value={c.usuario_envase} />
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value, mono, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${accent ? "text-[#00E5FF]" : "text-white"} text-sm mt-0.5 leading-tight`}>{value || "—"}</div>
    </div>
  );
}
